/**
 * SignatureOverlay Component
 * Component for displaying a signature pad in an overlay
 */
import React, { useState, useEffect } from "react";
import { View, StyleSheet, Platform, Text } from "react-native";
import LHOverlay from "../../../components/LHOverlay";
import LhButton from "../../../components/LhButton";
import SignatureModule from "../../../components/SignatureModule";
import LhDatePicker from "../../../components/LhDatePicker";
import { submitRackingInspection } from "../../../components/racking_inspection/submitRackingInspection";
import FontFamily from "../../../constants/fonts";
import { useTheme } from "../../../theme";

interface SignatureOverlayProps {
  visible: boolean;
  onDismiss: () => void;
  signatureData: any;
  onSubmitSuccess: () => void;
}

/**
 * Component to display signature capture in an overlay
 * @param props Component properties
 * @returns SignatureOverlay component
 */
const SignatureOverlay: React.FC<SignatureOverlayProps> = ({
  visible,
  onDismiss,
  signatureData,
  onSubmitSuccess,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [localSignatureData, setLocalSignatureData] = useState<any>(null);
  const [signature, setSignature] = useState<{
    fileName: string;
    imageUrl?: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [completionDate, setCompletionDate] = useState<Date | null>(new Date());

  // Helper function to format date to YYYY-MM-DD (avoiding timezone issues)
  const formatDateToYYYYMMDD = (date: Date): string => {
    if (!date || isNaN(date.getTime())) {
      return new Date().toISOString().split("T")[0];
    }

    // Use local date components to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // Update local data when signatureData changes or visibility changes
  useEffect(() => {
    if (visible && signatureData) {
      setLocalSignatureData(signatureData);
      // Reset signature when overlay opens
      setSignature(null);
      // Set completion date to current date when opening
      setCompletionDate(new Date());
    }
  }, [signatureData, visible]);

  // Reset state when overlay is dismissed
  const handleDismiss = () => {
    // Reset states
    setSignature(null);
    setLocalSignatureData(null);
    setCompletionDate(new Date());
    // Call parent dismiss handler
    onDismiss();
  };

  // Check if signature exists and completion date is selected
  const hasSignature = () => {
    return (
      signature !== null &&
      ((signature.imageUrl && signature.imageUrl.trim() !== "") ||
        (signature.fileName && signature.fileName.trim() !== "")) &&
      completionDate !== null
    );
  };

  // Handle signature change
  const handleSignatureChange = (
    sig: { imageUrl?: string; fileName: string } | null
  ) => {
    // Store signature separately for immediate UI feedback
    setSignature(sig);

    if (!localSignatureData) {
      return;
    }

    // Get the current completion date and format it properly
    const currentDate = completionDate || new Date();
    const formattedDate = formatDateToYYYYMMDD(currentDate);

    console.log(
      "SignatureOverlay: Signature changed, using completion date:",
      currentDate
    );
    console.log("SignatureOverlay: Formatted completion date:", formattedDate);

    // Update the signature in the data object with selected completion date
    setLocalSignatureData((prev: any) => {
      if (!prev) {
        return prev;
      }

      const newDM = Array.isArray(prev.DM) ? [...prev.DM] : [];
      if (newDM[0]) {
        // Set both IS_DOR_COMPLETE and DORCOMPLETION fields using selected completion date
        newDM[0] = {
          ...newDM[0],
          Signature: sig?.imageUrl || sig?.fileName || "",
          IS_DOR_COMPLETE: true,
          DORCOMPLETION: formattedDate,
        };
      }

      console.log(
        "SignatureOverlay: Updated localSignatureData with DORCOMPLETION:",
        formattedDate
      );
      return { ...prev, DM: newDM };
    });
  };

  // Handle completion date change
  const handleCompletionDateChange = (date: Date | null) => {
    console.log("SignatureOverlay: Date picker changed to:", date);
    setCompletionDate(date);

    // Update the signature data with the new completion date if signature already exists
    if (signature && localSignatureData) {
      const formattedDate = date
        ? formatDateToYYYYMMDD(date)
        : formatDateToYYYYMMDD(new Date());
      console.log(
        "SignatureOverlay: Updating existing signature data with date:",
        formattedDate
      );

      setLocalSignatureData((prev: any) => {
        if (!prev) {
          return prev;
        }

        const newDM = Array.isArray(prev.DM) ? [...prev.DM] : [];
        if (newDM[0]) {
          newDM[0] = {
            ...newDM[0],
            DORCOMPLETION: formattedDate,
          };
        }
        return { ...prev, DM: newDM };
      });
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    if (!hasSignature() || !localSignatureData) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Final check and update of the completion date before submission
      const finalDate = completionDate || new Date();
      const finalFormattedDate = formatDateToYYYYMMDD(finalDate);

      console.log(
        "SignatureOverlay: Final submission - completion date:",
        finalDate
      );
      console.log(
        "SignatureOverlay: Final submission - formatted date:",
        finalFormattedDate
      );

      // Ensure the DORCOMPLETION is in the correct format before submission
      const submissionData = {
        ...localSignatureData,
        DM: localSignatureData.DM
          ? localSignatureData.DM.map((item: any) => ({
              ...item,
              DORCOMPLETION: finalFormattedDate,
            }))
          : [],
      };

      console.log(
        "SignatureOverlay: Final submission data:",
        JSON.stringify(submissionData, null, 2)
      );

      await submitRackingInspection(submissionData);
      console.log("SignatureData submitted successfully:", submissionData);
      onSubmitSuccess();
      // Reset state after successful submission
      setSignature(null);
      setLocalSignatureData(null);
      setCompletionDate(new Date());
      handleDismiss();
    } catch (err) {
      console.error("Error submitting signature data:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Log signature state for debugging
  useEffect(() => {
    console.log("SignatureOverlay: Signature state changed:", signature);
    console.log("SignatureOverlay: hasSignature() =", hasSignature());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // Log completion date changes for debugging
  useEffect(() => {
    console.log(
      "SignatureOverlay: Completion date state changed:",
      completionDate
    );
    console.log(
      "SignatureOverlay: Formatted completion date:",
      completionDate ? formatDateToYYYYMMDD(completionDate) : "null"
    );
  }, [completionDate]);

  return (
    <LHOverlay
      visible={visible}
      onDismiss={handleDismiss}
      title="Complete Inspection"
      size="medium"
    >
      <View style={[styles.container, isDark && { backgroundColor: colors.background.surface }]}>
       {/* Completion Date Picker */}
       <View style={styles.datePickerContainer}>
         <Text style={[styles.datePickerLabel, isDark && { color: colors.text.inverse }]}>Completion Date *</Text>
          <LhDatePicker
            value={completionDate}
            onChange={handleCompletionDateChange}
            placeholder="Select completion date"
            label=""
            disabled={isSubmitting}
            colorScheme={isDark ? "dark" : "light"}
            iconColor={isDark ? "#FFFFFF" : "#555555"}
            style={{}}
          />
        </View>

        {/* Signature Pad */}
        <View style={styles.signatureContainer}>
          <Text style={[styles.signatureLabel, isDark && { color: colors.text.inverse }]}>Signature *</Text>
          <SignatureModule
            value={signature}
            onChange={handleSignatureChange}
            disabled={isSubmitting}
            containerName="racking"
          />
        </View>

        <View style={styles.filterOverlayButtons}>
          <LhButton
            text="Cancel"
            variant="secondary"
            size="small"
            style={{ ...styles.filterOverlayButton, ...styles.cancelButton }}
            onPress={handleDismiss}
            isLoading={false}
            disabled={isSubmitting}
            testID="cancel-button"
          />
          <LhButton
            text="Complete"
            variant="primary"
            size="small"
            onPress={handleSubmit}
            isLoading={false}
            disabled={!hasSignature() || isSubmitting}
            style={styles.filterOverlayButton}
            testID="proceed-button"
          />
        </View>
      </View>
    </LHOverlay>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
  datePickerContainer: {
    marginBottom: 20,
  },
  datePickerLabel: {
    fontSize: 16,
    fontWeight: "600",
    color: "#333",
    marginBottom: 8,
  },
  signatureContainer: {
    marginBottom: 20,
  },
  signatureLabel: {
    fontSize: 16,
    fontWeight: "600",
    color: "#333",
    marginBottom: 8,
  },
  filterOverlayButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: 20,
    gap: 10,
  },
  filterOverlayButton: {
    width: Platform.OS === "web" ? "15%" : "40%",
    fontSize: 16,
    fontFamily: FontFamily.regular,
  },
  cancelButton: {
    borderWidth: 0,
    backgroundColor: "transparent",
  },
});

export default SignatureOverlay;
